import { Button, Input, ScrollArea } from '@creanizer/ui'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedCharacter } from '@creanizer/types'
import { Search, Trash2, Users, Eye, Edit, Edit2, Download, Folder, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { ExportImportButtons } from '../../../../components/common'
import { CharacterEditModal } from './CharacterEditModal'
import { CharacterEditModalEnhanced } from './CharacterEditModalEnhanced'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedCharactersListProps {
  characters: SavedCharacter[]
  onLoad: (character: SavedCharacter) => void
  onUpdate?: (character: SavedCharacter) => void
}

export function SavedCharactersList({ characters, onLoad, onUpdate }: SavedCharactersListProps) {
  const { deleteCharacter, clearCharacters, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, updateCharacter } = useCharacterStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  const [search, setSearch] = useState('')
  const [editingCharacter, setEditingCharacter] = useState<SavedCharacter | null>(null)
  const [detailEditingCharacter, setDetailEditingCharacter] = useState<SavedCharacter | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)

  // フォルダによるフィルタリング
  let folderFilteredCharacters = characters
  if (selectedFolderId === 'all') {
    folderFilteredCharacters = characters
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('character')
    folders.forEach(folder => {
      getItemsInFolder('character', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredCharacters = characters.filter(character => !allFolderItemIds.has(character.id))
  } else {
    const itemIds = getItemsInFolder('character', selectedFolderId)
    folderFilteredCharacters = characters.filter(character => itemIds.includes(character.id))
  }
  
  // 検索によるフィルタリング
  const filteredCharacters = folderFilteredCharacters.filter(character =>
    character.title.toLowerCase().includes(search.toLowerCase()) ||
    character.result.name.toLowerCase().includes(search.toLowerCase()) ||
    character.result.role.toLowerCase().includes(search.toLowerCase()) ||
    character.tags?.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
  )

  // Export/Import handled by ExportImportButtons component

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みキャラクター ({characters.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したキャラクター設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="キャラクターを検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="キャラクター"
          />

          {characters.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのキャラクターを削除しますか？')) {
                  clearCharacters()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="character"
                items={characters}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('character', itemId)
                  moveItemBetweenFolders('character', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* キャラクターリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
          <div className="space-y-2 max-w-full w-full">
            {filteredCharacters.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {search ? '該当するキャラクターが見つかりません' : 'まだキャラクターが保存されていません'}
              </p>
            ) : (
              filteredCharacters.map((character) => (
                <div 
                  key={character.id} 
                  className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                  draggable
                  onDragStart={(e) => {
                    setDraggedItemId(character.id)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragEnd={() => {
                    setDraggedItemId(null)
                  }}
                  style={{
                    opacity: draggedItemId === character.id ? 0.5 : 1
                  }}
                >
                    {/* モバイル版: 縦並びレイアウト */}
                    <div className="flex flex-col gap-3 sm:hidden w-full">
                      <div className="w-full">
                        <h3 className="font-medium text-sm truncate">{character.title}</h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                          <span className="truncate">{character.result.name}</span>
                          <span>•</span>
                          <span>{character.result.age}</span>
                          <span>•</span>
                          <span className="truncate">{character.result.role}</span>
                        </div>
                        {character.tags && character.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {character.tags.slice(0, 2).map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(character.createdAt).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        
                        {/* フォルダ選択 */}
                        <div className="mt-2">
                          <FolderMoveSelect
                            generatorType="character"
                            itemId={character.id}
                            currentFolderId={getItemFolder('character', character.id)?.id}
                            onMove={() => {}}
                          />
                        </div>
                      </div>
                      
                      {/* モバイル版: 横一列のボタン */}
                      <div className="flex gap-0.5 justify-start w-full flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad(character)}
                          title="読込"
                          className="w-12 h-7 px-1"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        {onUpdate && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onUpdate(character)}
                            title="情報更新"
                            className="w-12 h-7 px-1"
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingCharacter(character)}
                          title="簡易編集"
                          className="w-12 h-7 px-1"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDetailEditingCharacter(character)}
                          title="詳細編集"
                          className="w-12 h-7 px-1"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(character.id)}
                          title="テキスト形式でエクスポート"
                          className="w-14 h-7 px-0.5 text-[9px]"
                        >
                          <Download className="h-2.5 w-2.5" />
                          <span className="ml-0.5">txt</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(character.id)}
                          title="JSON形式でエクスポート"
                          className="w-14 h-7 px-0.5 text-[9px]"
                        >
                          <Download className="h-2.5 w-2.5" />
                          <span className="ml-0.5">json</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (confirm('このキャラクターを削除しますか？')) {
                              deleteCharacter(character.id)
                            }
                          }}
                          className="w-12 h-7 px-1"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* PC版: 既存の横並びレイアウト */}
                    <div className="hidden sm:flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="font-medium text-sm truncate">{character.title}</h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                          <span className="truncate">{character.result.name}</span>
                          <span>•</span>
                          <span>{character.result.age}</span>
                          <span>•</span>
                          <span className="truncate">{character.result.role}</span>
                        </div>
                        {character.tags && character.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {character.tags.slice(0, 2).map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(character.createdAt).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onLoad(character)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            読込
                          </Button>
                          {onUpdate && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onUpdate(character)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              情報更新
                            </Button>
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCharacter(character)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            簡易編集
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDetailEditingCharacter(character)}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            詳細編集
                          </Button>
                        </div>

                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportSingleAsText(character.id)}
                            title="テキスト形式でエクスポート"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            txt
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportSingleAsJSON(character.id)}
                            title="JSON形式でエクスポート"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            json
                          </Button>
                        </div>

                        <div className="flex flex-col gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm('このキャラクターを削除しますか？')) {
                                deleteCharacter(character.id)
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                </div>
              ))
            )}
          </div>
            </ScrollArea>
          </div>
        </div>

        {characters.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingCharacter && (
        <CharacterEditModal
          isOpen={!!editingCharacter}
          onClose={() => setEditingCharacter(null)}
          onSave={(updatedResult) => {
            updateCharacter(editingCharacter.id, {
              result: { ...editingCharacter.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingCharacter(null)
          }}
          characterResult={editingCharacter.result}
          title={`「${editingCharacter.title}」を簡易編集`}
        />
      )}
      
      {detailEditingCharacter && (
        <CharacterEditModalEnhanced
          isOpen={!!detailEditingCharacter}
          onClose={() => setDetailEditingCharacter(null)}
          onSave={(updatedResult) => {
            updateCharacter(detailEditingCharacter.id, {
              result: updatedResult,
              updatedAt: new Date().toISOString()
            })
            setDetailEditingCharacter(null)
          }}
          characterResult={detailEditingCharacter.result}
          title={`「${detailEditingCharacter.title}」を詳細編集`}
        />
      )}
    </div>
  )
}